import { getData, TableDataQuery, TableDataResponse } from '../get-table'
import { HttpService } from '../http.service'
import { API_PAYROLL, ENV } from '../../env'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'

export type RemoteApiName = 'payroll' | 'legacy'

type RemoteSourceFilterValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | {
      operator: 'not-null'
    }

interface RemoteSourceBase {
  idKey?: string
  nameKey?: string
  filter?: Record<string, RemoteSourceFilterValue>
}

export interface RemoteSourceGetTable extends RemoteSourceBase {
  type: 'get-table'
  query: TableDataQuery
}

export interface RemoteSourceApi extends RemoteSourceBase {
  type: 'api'
  api?: RemoteApiName
  path: string
  params?: Record<string, string>
  headers?: Record<string, string>
}

export type RemoteSource = RemoteSourceGetTable | RemoteSourceApi

export interface RemoteColumn {
  name: string
  title: string
  source?: string
}

export type RemoteFieldType = 'select' | 'check' | 'string' | 'number'

export interface RemoteField {
  name: string
  type: RemoteFieldType
  label: string
  placeholder?: string
  source?: string
  required?: boolean
  editable?: boolean
  default?: unknown
  dependsOn?: string
  resets?: string[]
  filterBy?: RemoteOptionFilter
}

/* Conserva las opciones cuya propiedad optionKey coincide con la propiedad sourceKey del ítem elegido en otro campo */
export interface RemoteOptionFilter {
  field: string
  sourceKey: string
  optionKey: string
}

export interface RemoteTableMessages {
  add?: string
  edit?: string
  remove?: string
  removeQuestion?: string
  saveParentFirst?: string
  emptySources?: string
  created?: string
  updated?: string
  removed?: string
  errors?: Record<string, string>
}

export interface RemoteTableConfig {
  api?: RemoteApiName
  resource: string
  idKey?: string
  listParams?: Record<string, string>
  listHeaders?: Record<string, string>
  createBody?: Record<string, unknown>
  actions?: string[]
  uniqueBy?: string[]
  sources?: Record<string, RemoteSource>
  columns: RemoteColumn[]
  fields: RemoteField[]
  messages?: RemoteTableMessages
}

export type RemoteRow = Record<string, unknown>

export interface SourceItem {
  id: string
  name: string
  raw: RemoteRow
}

export interface TagContext {
  currentId?: string
  model?: Record<string, unknown>
}

const TAG_PATTERN = /:(currentId|countryId|companyId|payrollId|workspaceId)\b/g
const MODEL_TAG_PATTERN = /:model\.([A-Za-z0-9_]+)/g

const storeTags = (): Record<string, string> => {
  const payroll = usePayrollStore().payroll
  return {
    countryId: payroll?.country.id ?? '',
    companyId: payroll?.company.id ?? '',
    payrollId: payroll?.id ?? '',
    workspaceId: useUserStore().user?.workspaceId ?? ''
  }
}

const asText = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value)

function resolveString(text: string, ctx: TagContext, quote: boolean): string {
  const tags: Record<string, string> = {
    currentId: ctx.currentId ?? '',
    ...storeTags()
  }
  const wrap = (value: unknown) =>
    quote ? `'${asText(value)}'` : asText(value)
  return text
    .replace(MODEL_TAG_PATTERN, (_, key: string) => wrap(ctx.model?.[key]))
    .replace(TAG_PATTERN, (_, key: string) => wrap(tags[key]))
}

/* Reemplaza :currentId, :countryId, :companyId, :payrollId, :workspaceId y :model.<campo>. Con quote=true envuelve el valor en comillas simples para SQL. */
export function resolveTags<T>(value: T, ctx: TagContext, quote = false): T {
  if (typeof value === 'string') {
    return resolveString(value, ctx, quote) as unknown as T
  }
  if (Array.isArray(value)) {
    return value.map(item => resolveTags(item, ctx, quote)) as unknown as T
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        resolveTags(item, ctx, quote)
      ])
    ) as T
  }
  return value
}

export const usesCurrentId = (config: RemoteTableConfig): boolean =>
  JSON.stringify(config).includes(':currentId')

export const matchesSourceFilter = (
  raw: RemoteRow,
  filter?: Record<string, RemoteSourceFilterValue>
): boolean =>
  !filter ||
  Object.entries(filter).every(([key, expected]) => {
    if (
      expected &&
      typeof expected === 'object' &&
      !Array.isArray(expected) &&
      'operator' in expected
    ) {
      if (expected.operator === 'not-null') {
        return raw[key] !== undefined && raw[key] !== null && raw[key] !== ''
      }
    }

    return asText(raw[key]) === asText(expected)
  })

/* API GW del nuevo modulo de payroll. El API legacy reutiliza la instancia por defecto de HttpService. */
const payrollHttp = new HttpService(API_PAYROLL[ENV()]())
const legacyHttp = new HttpService()

const httpFor = (api: RemoteApiName = 'payroll'): HttpService =>
  api === 'payroll' ? payrollHttp : legacyHttp

const baseHeaders = (api: RemoteApiName = 'payroll'): Record<string, string> =>
  api === 'payroll'
    ? { 'x-workspace-id': useUserStore().user?.workspaceId ?? '' }
    : {}

/*
 * HttpService sobreescribe los headers de payroll con los del store, asi que las
 * cabeceras declaradas en la configuracion solo se respetan en lecturas (GET),
 * que se envian con el juego completo de headers construido aqui.
 */
const fullHeaders = (api: RemoteApiName): Record<string, string> => {
  const token = useUserStore().user?.token?.token
  const payroll = usePayrollStore().payroll
  return {
    ...baseHeaders(api),
    Authorization: token ? `Bearer ${token}` : '',
    'x-country-id': payroll?.country.id ?? '',
    'x-country-code': payroll?.country.iso_code ?? '',
    'x-company-id': payroll?.company.id ?? '',
    'x-payroll-id': payroll?.id ?? '',
    'x-channel': 'APS-WEB',
    'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone
  }
}

async function remoteGet<T>(
  api: RemoteApiName = 'payroll',
  path: string,
  params: Record<string, string> = {},
  headers: Record<string, string> = {}
): Promise<T> {
  const http = httpFor(api)
  if (Object.keys(headers).length === 0) {
    const response = await http.get<T>(path, {
      params,
      headers: baseHeaders(api)
    })
    return response.data
  }
  const response = await http.getWithHeaders<T>(path, {
    params,
    headers: { ...fullHeaders(api), ...headers }
  })
  return response.data
}

export async function listRemoteRows(
  config: RemoteTableConfig,
  ctx: TagContext
): Promise<RemoteRow[]> {
  return remoteGet<RemoteRow[]>(
    config.api,
    config.resource,
    resolveTags(config.listParams ?? {}, ctx),
    resolveTags(config.listHeaders ?? {}, ctx)
  )
}

export async function createRemoteRow(
  config: RemoteTableConfig,
  ctx: TagContext,
  values: RemoteRow
): Promise<RemoteRow> {
  const response = await httpFor(config.api).post<RemoteRow, RemoteRow>(
    config.resource,
    { ...resolveTags(config.createBody ?? {}, ctx), ...values },
    { headers: baseHeaders(config.api) }
  )
  return response.data
}

export async function updateRemoteRow(
  config: RemoteTableConfig,
  id: string,
  values: RemoteRow
): Promise<RemoteRow> {
  const response = await httpFor(config.api).put<RemoteRow, RemoteRow>(
    `${config.resource}/${id}`,
    values,
    { headers: baseHeaders(config.api) }
  )
  return response.data
}

export async function deleteRemoteRow(
  config: RemoteTableConfig,
  id: string
): Promise<void> {
  await httpFor(config.api).delete(`${config.resource}/${id}`, {
    headers: baseHeaders(config.api)
  })
}

const toSourceItems = (rows: RemoteRow[], source: RemoteSource): SourceItem[] =>
  rows.map(raw => ({
    id: asText(raw[source.idKey ?? 'id']),
    name: asText(raw[source.nameKey ?? 'name']),
    raw
  }))

export async function loadRemoteSource(
  source: RemoteSource,
  ctx: TagContext
): Promise<SourceItem[]> {
  if (source.type === 'get-table') {
    const [response] = await getData<[TableDataResponse<RemoteRow>]>(
      resolveTags(source.query, ctx, true)
    )
    return toSourceItems(response?.results ?? [], source)
  }
  const rows = await remoteGet<RemoteRow[]>(
    source.api,
    source.path,
    resolveTags(source.params ?? {}, ctx),
    resolveTags(source.headers ?? {}, ctx)
  )
  return toSourceItems(rows, source)
}
